<template>
  <div class="subscriptions_answers_overview">
    <ag-grid-table
      class="ag-grid"
      :grid-options="gridOptions"
      :column-defs="columnDefs"
      :row-data="rowData"
      :paginate="false"
      :fit-cols="false"
      :filter="true"
      :search="true"
      @gridSizeChanged="gridSizeChanged"
      @showFilter="showFilterChange"
    >
      <template slot="filter">
        <slot
          name="filter"
        />
      </template>
    </ag-grid-table>
  </div>
</template>

<script>
import 'ag-grid-community/styles/ag-grid.css'
import AgGridTable from '@/employees/components/AgGridTable'
import SubscribableHeader from '@/employees/components/Subscribable/SubscribableHeader.vue'
import { mapState } from 'vuex'
import { studentSortColumns } from '@/helpers'

export default {
  name: 'SubscriptionsAnswersMatrixOverview',

  components: { AgGridTable, SubscribableHeader },

  props: {
    subscribables: {
      required: true,
      type: Array,
    },
    studentSubscribables: {
      required: true,
      type: Object,
    },
    questionTemplateIds: {
      required: false,
      type: Array,
      default: null,
    },
  },

  data () {
    return {
      gridOptions: {
        defaultColDef: {
          sortable: false,
          resizable: false,
        },
        headerHeight: 60,
        rowBuffer: 25,
        rowHeight: 60,
        suppressCellFocus: true,
        suppressHeaderFocus: true,
        enableBrowserTooltips: true,
        pagination: false,
      },
      pinning: true,
    }
  },

  computed: {
    ...mapState({
      students (state) {
        return state.students.students
      },

      studentGroups (state) {
        const studentGroups = []

        this.subscribables.forEach(subscribable => {
          const studentGroupsSubscribable = subscribable.studentGroups

          studentGroupsSubscribable.forEach(group => {
            if (!studentGroups.includes(parseInt(group))) {
              studentGroups.push(parseInt(group))
            }
          })
        })

        return Object.values(state.groups.groups).filter(group => studentGroups.includes(parseInt(group.id)))
      },
    }),

    columnDefs () {
      const subscribables = this.subscribables
      const columns = subscribables.sort((a, b) => new Date(a.startdate) - new Date(b.startdate)) // sort ascending
        .map(subscribable => ({
          headerName: subscribable.name,
          field: `${subscribable.id}`,
          cellRenderer: this.renderSubscriptionCell,
          headerComponent: 'SubscribableHeader',
          headerComponentParams: { id: subscribable.id, name: subscribable.name, link: subscribable.link, type: subscribable.type },
          cellClass: 'status',
          getQuickFilterText: () => null,
          width: 100,
          cellStyle: this.cellStyle,
        }))

      return [
        {
          headerName: Lang.get('core.name'),
          field: 'name',
          pinned: 'left',
          cellRenderer: this.renderStudentCell,
          valueFormatter: params => params.value.name,
          getQuickFilterText: (params) => {
            return params.value
          },
          cellStyle: this.cellStyle,
        },
        ...columns,
      ]
    },

    rowData () {
      return this.sortedStudents.map((student) => {
        const subscribableRows = []

        this.subscribables.forEach((subscribable) => {
          let studentSubscribable = null
          if (typeof this.studentSubscribables[student.id] !== 'undefined') {
            studentSubscribable = Object.values(this.studentSubscribables[student.id]).find(studentSub => {
              return Number(studentSub.subscribable_id) === Number(subscribable.id) &&
                  studentSub.subscribed === true
            })
          }

          subscribableRows[subscribable.id] = {
            studentSubscribable,
          }
        })

        return {
          name: student,
          ...subscribableRows,
        }
      })
    },

    cellStyle () {
      return { 'font-size': '14px', padding: '5px', 'font-family': 'Roboto' }
    },

    sortedStudents () {
      return _.orderBy(Object.values(this.students), studentSortColumns())
    },
  },

  methods: {
    renderSubscriptionCell (data) {
      const div = document.createElement('div')

      const studentSubscribable = data.value.studentSubscribable
      if (studentSubscribable != null && this.questionTemplateIds.length > 0) {
        const answers = []
        this.questionTemplateIds.map(id => {
          if (studentSubscribable.parsed_answers[id] != null) {
            answers.push(_.escape(studentSubscribable.parsed_answers[id]))
          }
        })

        if (answers.length > 1) {
          div.innerHTML = '<span title="' + answers.join(' | ') + '">' + answers.join('</br>') + '</span>'
        } else if (answers.length === 1) {
          div.innerHTML = '<span>' + answers[0] + '</span>'
        }
      }

      return div
    },

    renderStudentCell (student) {
      const div = document.createElement('div')

      const groupNames = this.studentGroups.filter(group => group.students.includes(student.value.id))
        .map(group => group.name)
        .join(', ')

      div.innerHTML = `<span class="studentbutton" style="cursor:pointer"><b>${_.escape(student.value.name)}</b>` + (groupNames.length < 1 ? '' : `<br><small>${_.escape(groupNames)}</small></span>`)

      const button = div.querySelectorAll('.studentbutton')[0]

      button.addEventListener('click', function () {
        window.open(student.value.link)
      })

      return div
    },

    gridSizeChanged (data) {
      const availableWidth = data.clientWidth
      const totalColumnWidth = data.api.getAllDisplayedColumns()
        .reduce((total, column) => total + column.actualWidth, 0)

      this.pinning = availableWidth < totalColumnWidth
    },

    showFilterChange (event) {
      this.$emit('showFilter', event)
    },
  },
}
</script>

<style lang="scss">
.subscriptions_answers_overview {
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  height: inherit;

  .ag-grid {
    display: flex;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: auto;
    height: inherit;
  }

  .ag-row .ag-cell {
    display: flex;
    align-items: center;

    &.status {

      justify-content: center; /* align horizontal */

    }
  }

  .ag-row {
    border-top: 1px solid #dee2e6;
  }

  .ag-cell {
    line-height: 20px !important;
  }

  .ag-horizontal-left-spacer, .ag-horizontal-right-spacer {
    visibility: hidden;
  }
}
</style>
